import { ActionReducerMapBuilder, PayloadAction, createSlice } from "@reduxjs/toolkit";
import reducers from "./reducers";
import { sendSecurityPrices } from "./asyncThunks";

import { SecuritiesUpdatedStagedState } from "./types";
import { setErrorInSpecificYield } from "../producerServices";
import { YieldType } from "../globalTypes";

import * as Sentry from "@sentry/react";
import { PricesResendManager } from "../../classes/pricesResendManager";

const initialState: SecuritiesUpdatedStagedState = {
  sendingValues: false,
  stagedSecuritiesValuesUpdates: {},
  securitiesValues: {},
};

interface SendSecurityPricesFulfilledPayload {
  updatedRow: number;
  errorsInYields: { yieldType: YieldType }[];
  securityProducerId?: string;
  yieldsSentSuccessfully?: YieldType[];
}

const options = {
  name: "securitiesUpdatedStaged",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<SecuritiesUpdatedStagedState>) => {
    builder.addCase(sendSecurityPrices.pending, (state: SecuritiesUpdatedStagedState) => {
      state.sendingValues = true;
    });
    builder.addCase(
      sendSecurityPrices.fulfilled,
      (state: SecuritiesUpdatedStagedState, action: PayloadAction<SendSecurityPricesFulfilledPayload>) => {
        const { updatedRow: sentRow, errorsInYields, securityProducerId, yieldsSentSuccessfully } = action.payload;

        state.sendingValues = false;

        if (errorsInYields.length) {
          errorsInYields.forEach((errorInYield) => {
            setErrorInSpecificYield({ yieldType: errorInYield.yieldType, sentRow: sentRow - 1 });
          });
        }

        if (!securityProducerId || !yieldsSentSuccessfully) return;

        PricesResendManager.getInstance().removeErrorSentWithOtherValue({
          securityProducerId,
          yieldsSentSuccessfully,
        });
      }
    );
    builder.addCase(sendSecurityPrices.rejected, (state: SecuritiesUpdatedStagedState) => {
      state.sendingValues = false;

      Sentry.captureException({
        message: "Error while sending security prices",
        level: "error",
        data: state,
      });
    });
  },
};

const securitiesUpdatedStagedSlice = createSlice(options);

export const {
  addSecurityToStagingArea,
  moveSecurityFromStagingToSecuritiesValues,
  managePricesWithErrorSending,
  setStagingState,
  setSecuritiesValuesState,
  removeSecurityFromStagingAndSecuritiesValues,
  updateRowsInStagingAreaAfterDeletingSecurities,
  removeSecurityFromStagingArea,
  setQuoteFeedOfSecurityInPricesSentAsNone,
} = securitiesUpdatedStagedSlice.actions;

export const selectSecuritiesValuesInStaging = (state: { securitiesUpdatedStaged: SecuritiesUpdatedStagedState }) =>
  state.securitiesUpdatedStaged.stagedSecuritiesValuesUpdates;
export const selectSecuritiesValues = (state: { securitiesUpdatedStaged: SecuritiesUpdatedStagedState }) =>
  state.securitiesUpdatedStaged.securitiesValues;
export const selectSendingValues = (state: { securitiesUpdatedStaged: SecuritiesUpdatedStagedState }) =>
  state.securitiesUpdatedStaged.sendingValues;

export default securitiesUpdatedStagedSlice.reducer;
